
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { SeekBrandSelect } from './branding';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "branding"
    }}>{`Branding`}</h1>
    <p>{`Branding allows SEEK hirers to promote their company brand on their job ads.
Not all `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/ad-selection"
      }}>{`SEEK ad products`}</a>{` include branding;
you can query the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/SeekAnzAdProductFeatures"
      }}>{`ad product’s features`}</a>{` to determine if branding is available.`}</p>
    <p>{`A brand has four components:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`An opaque `}<a parentName="p" {...{
            "href": "/graphql/seek-api-conventions#object-identifiers"
          }}>{`object identifier`}</a>{` used to specify the brand when `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/managing-job-ads/posting-a-job-ad"
          }}>{`posting a job ad`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`A mandatory logo displayed on the hirer’s job ads and on SEEK’s Apply Form.
This is represented as an `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/AdvertisementBrandingImage"
          }}><inlineCode parentName="a">{`AdvertisementBrandingImage`}</inlineCode></a>{` with a type code of `}<inlineCode parentName="p">{`OriginalLogo`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`An optional cover image displayed at the top of the hirer’s job ads.
This is represented as an `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/AdvertisementBrandingImage"
          }}><inlineCode parentName="a">{`AdvertisementBrandingImage`}</inlineCode></a>{` with a type code of `}<inlineCode parentName="p">{`CoverImage`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`A display `}<inlineCode parentName="p">{`name`}</inlineCode>{` used for the hirer’s reference when selecting and managing brands.`}</p>
      </li>
    </ol>
    <p>{`Brands on the SEEK employer website are identified visually using their logo & cover image.
The brand’s name is rarely descriptive enough to uniquely identify the brand.
SEEK recommends that your software presents hirers with a paginated list of brands that incorporates their logo, cover image & name.`}</p>

    <SeekBrandSelect variant="multiple-brands" mdxType="SeekBrandSelect" />
    <p>{`The SEEK API only supports `}<a parentName="p" {...{
        "href": "#advertisementbrandings"
      }}>{`listing`}</a>{` and `}<a parentName="p" {...{
        "href": "#advertisementbranding"
      }}>{`querying`}</a>{` brands.
Hirers must use the Logos & Brands section of the SEEK employer website to manage their brands.`}</p>
    <p>{`You will receive an empty array when listing brands for a hirer that has none configured.
In this scenario,
you should display a call to action in place of the brand selection control.
A localised deep link to the SEEK employer website can be queried from the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/AdvertisementBrandingsConnection/field/brandManagementUrl"
      }}><inlineCode parentName="a">{`AdvertisementBrandingsConnection.brandManagementUrl`}</inlineCode></a>{` field.`}</p>
    <SeekBrandSelect variant="no-brands" mdxType="SeekBrandSelect" />
    <p>{`Hirers can set a default brand on the SEEK employer website,
which is applied to job ads that support branding but were posted without one selected.
This is handled transparently to your posting process.`}</p>
    <p>{`When hirers create, update or delete a brand they expect the change to be reflected the next time they post a job ad.
To support this, you should query the hirer’s current brands as part of your software’s job posting flow.
If this isn’t possible you should provide a way for the hirer to manually refresh their brands from the SEEK API.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`SEEK recommends using a `}<a parentName="p" {...{
            "href": "/auth/browser-tokens"
          }}>{`browser token`}</a>{` to query brands directly from a hirer’s browser.
For the browser token to work, you will need to include the `}<inlineCode parentName="p">{`query:advertisement-brandings`}</inlineCode>{` scope in your request.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You can also query brands from your backend using a `}<a parentName="p" {...{
            "href": "/auth/partner-tokens"
          }}>{`partner token`}</a>{`.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "operations"
    }}>{`Operations`}</h2>
    <p>{`The SEEK API provides two queries for retrieving brands:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#advertisementbrandings"
          }}><inlineCode parentName="a">{`advertisementBrandings`}</inlineCode></a>{` returns a paginated list of brands for a given hirer.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#advertisementbranding"
          }}><inlineCode parentName="a">{`advertisementBranding`}</inlineCode></a>{` returns a brand for a given brand object identifier.`}</p>
      </li>
    </ol>
    <h3 {...{
      "id": "advertisementbrandings"
    }}>{`advertisementBrandings`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/advertisementBrandings"
      }}><inlineCode parentName="a">{`advertisementBrandings`}</inlineCode>{` query`}</a>{` returns a `}<a parentName="p" {...{
        "href": "/graphql/seek-api-conventions#cursor-based-pagination"
      }}>{`paginated list`}</a>{` of brands for a given hirer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($after: String, $first: Int, $hirerId: String!) {\\n  advertisementBrandings(after: $after, first: $first, hirerId: $hirerId) {\\n    edges {\\n      node {\\n        id {\\n          value\\n        }\\n        name\\n        images {\\n          typeCode\\n          url\\n        }\\n      }\\n    }\\n    brandManagementUrl {\\n      url\\n    }\\n    pageInfo {\\n      hasNextPage\\n      endCursor\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":77,\"column\":1,\"offset\":3807},\"end\":{\"line\":101,\"column\":4,\"offset\":4207},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"after\\\": null,\\n  \\\"first\\\": 2,\\n  \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":103,\"column\":1,\"offset\":4209},\"end\":{\"line\":109,\"column\":4,\"offset\":4315},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"advertisementBrandings\\\": {\\n    \\\"edges\\\": [\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:METPmG2UG8HathjkUf4yA\\\"\\n          },\\n          \\\"name\\\": \\\"SEEK logo - Town\\\",\\n          \\\"images\\\": [\\n            {\\n              \\\"typeCode\\\": \\\"OriginalLogo\\\",\\n              \\\"url\\\": \\\"https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8\\\"\\n            },\\n            {\\n              \\\"typeCode\\\": \\\"CoverImage\\\",\\n              \\\"url\\\": \\\"https://image-service-cdn.seek.com.au/2df39384e6fe569f73073fe138027b6c0a1e7658\\\"\\n            }\\n          ]\\n        }\\n      },\\n      {\\n        \\\"node\\\": {\\n          \\\"id\\\": {\\n            \\\"value\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\"\\n          },\\n          \\\"name\\\": \\\"SEEK logo - No cover image\\\",\\n          \\\"images\\\": [\\n            {\\n              \\\"typeCode\\\": \\\"OriginalLogo\\\",\\n              \\\"url\\\": \\\"https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8\\\"\\n            }\\n          ]\\n        }\\n      }\\n    ],\\n    \\\"brandManagementUrl\\\": {\\n      \\\"url\\\": \\\"https://talent.seek.com.au/account/branding\\\"\\n    },\\n    \\\"pageInfo\\\": {\\n      \\\"hasNextPage\\\": true,\\n      \\\"endCursor\\\": \\\"globalPublicTest:advertisementBrandingCursor:indirectBranding:VtB5VXd\\\"\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":4317},\"end\":{\"line\":157,\"column\":4,\"offset\":5641},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($after:": true,
        "String,": true,
        "$first:": true,
        "Int,": true,
        "$hirerId:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "advertisementBrandings(after:": true,
        "$after,": true,
        "first:": true,
        "$first,": true,
        "hirerId:": true,
        "$hirerId)": true,
        "edges": true,
        "node": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "name\\n": true,
        "images": true,
        "typeCode\\n": true,
        "url\\n": true,
        "brandManagementUrl": true,
        "pageInfo": true,
        "hasNextPage\\n": true,
        "endCursor\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":77,\"column\":1,\"offset\":3807},\"end\":{\"line\":101,\"column\":4,\"offset\":4207},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"after\\\":": true,
        "null,\\n": true,
        "\\\"first\\\":": true,
        "2,\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\"\\n}\",\"position\":{\"start\":{\"line\":103,\"column\":1,\"offset\":4209},\"end\":{\"line\":109,\"column\":4,\"offset\":4315},\"indent\":[1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"advertisementBrandings\\\":": true,
        "\\\"edges\\\":": true,
        "[\\n": true,
        "\\\"node\\\":": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:METPmG2UG8HathjkUf4yA\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"SEEK": true,
        "logo": true,
        "-": true,
        "Town\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"typeCode\\\":": true,
        "\\\"OriginalLogo\\\",\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8\\\"\\n": true,
        "\\\"CoverImage\\\",\\n": true,
        "\\\"https://image-service-cdn.seek.com.au/2df39384e6fe569f73073fe138027b6c0a1e7658\\\"\\n": true,
        "]\\n": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L\\\"\\n": true,
        "No": true,
        "cover": true,
        "image\\\",\\n": true,
        "],\\n": true,
        "\\\"brandManagementUrl\\\":": true,
        "\\\"https://talent.seek.com.au/account/branding\\\"\\n": true,
        "\\\"pageInfo\\\":": true,
        "\\\"hasNextPage\\\":": true,
        "true,\\n": true,
        "\\\"endCursor\\\":": true,
        "\\\"globalPublicTest:advertisementBrandingCursor:indirectBranding:VtB5VXd\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":111,\"column\":1,\"offset\":4317},\"end\":{\"line\":157,\"column\":4,\"offset\":5641},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($after: String, $first: Int, $hirerId: String!) {
  advertisementBrandings(after: $after, first: $first, hirerId: $hirerId) {
    edges {
      node {
        id {
          value
        }
        name
        images {
          typeCode
          url
        }
      }
    }
    brandManagementUrl {
      url
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
`}</code></pre>
    <p>{`For subsequent queries you pass the previous page’s `}<inlineCode parentName="p">{`endCursor`}</inlineCode>{` as the `}<inlineCode parentName="p">{`after`}</inlineCode>{` argument.
Once `}<inlineCode parentName="p">{`hasNextPage`}</inlineCode>{` is `}<inlineCode parentName="p">{`false`}</inlineCode>{` there are no more brands to page through.`}</p>
    <h3 {...{
      "id": "advertisementbranding"
    }}>{`advertisementBranding`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/advertisementBranding"
      }}><inlineCode parentName="a">{`advertisementBranding`}</inlineCode>{` query`}</a>{` returns the brand for a given brand object identifier.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  advertisementBranding(id: $id) {\\n    id {\\n      value\\n    }\\n    name\\n    images {\\n      typeCode\\n      url\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":168,\"column\":1,\"offset\":6022},\"end\":{\"line\":181,\"column\":4,\"offset\":6180},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e\\\"\\n}\",\"position\":{\"start\":{\"line\":183,\"column\":1,\"offset\":6182},\"end\":{\"line\":187,\"column\":4,\"offset\":6283},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"advertisementBranding\\\": {\\n    \\\"id\\\": {\\n      \\\"value\\\": \\\"globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e\\\"\\n    },\\n    \\\"name\\\": \\\"SEEK logo - Laptop\\\",\\n    \\\"images\\\": [\\n      {\\n        \\\"typeCode\\\": \\\"OriginalLogo\\\",\\n        \\\"url\\\": \\\"https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8\\\"\\n      },\\n      {\\n        \\\"typeCode\\\": \\\"CoverImage\\\",\\n        \\\"url\\\": \\\"https://image-service-cdn.seek.com.au/f33d107cd083880157439477e7790edd629ed50a\\\"\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":189,\"column\":1,\"offset\":6285},\"end\":{\"line\":208,\"column\":4,\"offset\":6796},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "advertisementBranding(id:": true,
        "$id)": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "name\\n": true,
        "images": true,
        "typeCode\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":168,\"column\":1,\"offset\":6022},\"end\":{\"line\":181,\"column\":4,\"offset\":6180},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e\\\"\\n}\",\"position\":{\"start\":{\"line\":183,\"column\":1,\"offset\":6182},\"end\":{\"line\":187,\"column\":4,\"offset\":6283},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"advertisementBranding\\\":": true,
        "\\\"value\\\":": true,
        "\\\"globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e\\\"\\n": true,
        "},\\n": true,
        "\\\"name\\\":": true,
        "\\\"SEEK": true,
        "logo": true,
        "-": true,
        "Laptop\\\",\\n": true,
        "\\\"images\\\":": true,
        "[\\n": true,
        "\\\"typeCode\\\":": true,
        "\\\"OriginalLogo\\\",\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8\\\"\\n": true,
        "\\\"CoverImage\\\",\\n": true,
        "\\\"https://image-service-cdn.seek.com.au/f33d107cd083880157439477e7790edd629ed50a\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":189,\"column\":1,\"offset\":6285},\"end\":{\"line\":208,\"column\":4,\"offset\":6796},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  advertisementBranding(id: $id) {
    id {
      value
    }
    name
    images {
      typeCode
      url
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;